/**
 * Admin Service
 * Handles admin-specific operations like user management, extensions, etc.
 */

import { userService } from './unifiedUserService';
import { ceuService } from './unifiedCEUService';

export const adminService = {
  /**
   * Get all users (admin only)
   */
  getAllUsers: async () => {
    try {
      // For admin functions, use direct DynamoDB access to avoid API limitations
      // This bypasses the individual user API restrictions
      const dynamoUserService = await import('./dynamoUserService');
      const result = await dynamoUserService.default.getAllUsers();
      
      // Ensure proper format
      if (result && result.success !== undefined) {
        return result;
      } else if (result && Array.isArray(result.users)) {
        return { success: true, users: result.users };
      } else if (Array.isArray(result)) {
        return { success: true, users: result };
      } else {
        return { success: true, users: result ? [result] : [] };
      }
    } catch (error) {
      console.error('Admin getAllUsers error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Create new user (admin only)
   */
  createUser: async (userData) => {
    try {
      const newUser = {
        ...userData,
        userId: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        isActive: true,
        profileCompleted: false,
        role: userData.role || 'user'
      };

      const result = await userService.saveUserProfile(newUser);
      
      if (result.success) {
                return { success: true, data: newUser };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get system analytics (admin only) - OPTIMIZED VERSION
   */
  getAnalytics: async () => {
    try {
      // Get users with direct DynamoDB access (consistent with getAllUsers change)
      const dynamoUserService = await import('./dynamoUserService');
      const usersResult = await dynamoUserService.default.getAllUsers();
      const users = usersResult.success ? usersResult.users : [];
      
      // Calculate user metrics
      const totalUsers = users.length;
      const adminUsers = users.filter(u => u.role === 'admin' || u.isAdmin).length;
      
      // Active users = users with profile updated in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      let activeUsers = 0;
      
      // Calculate active users from profile updates (avoid N+1 CEU queries)
      for (const user of users) {
        const profileUpdatedRecently = user.updatedAt && new Date(user.updatedAt) >= thirtyDaysAgo;
        if (profileUpdatedRecently) {
          activeUsers++;
        }
      }
      
      // Get CEU metrics with single scan operation
      let totalWorkshops = 0;
      let totalCEUHours = 0;
      
      try {
        // Use unified CEU service for consistency
        const allCEUsResult = await ceuService.getAllCEUs?.() || { success: false };
        if (allCEUsResult.success) {
          const allCEUs = allCEUsResult.data || [];
          totalWorkshops = allCEUs.length;
          totalCEUHours = allCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
          
          // Count users with recent CEU activity
          const usersWithRecentCEUs = new Set();
          allCEUs.forEach(ceu => {
            const ceuDate = new Date(ceu.createdAt || ceu.date);
            if (ceuDate >= thirtyDaysAgo) {
              usersWithRecentCEUs.add(ceu.userId);
            }
          });
          
          // Add users with recent CEU activity to active count
          activeUsers += usersWithRecentCEUs.size;
        } else {
          console.warn('Could not load CEU data for analytics, using user activity only');
        }
      } catch (error) {
        console.error('Error loading CEU analytics:', error);
        // Continue with user-only analytics
      }
      
      return {
        success: true,
        analytics: {
          userMetrics: {
            totalUsers,
            adminUsers,
            activeUsers
          },
          ceuMetrics: {
            totalWorkshops,
            totalCEUHours
          }
        }
      };
    } catch (error) {
      console.error('Error getting analytics:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Deactivate user (recommended over deletion)
   */
  deactivateUser: async (userId, reason, adminUserId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const updatedUser = {
        ...userResult.data,
        isActive: false,
        deactivatedAt: new Date().toISOString(),
        deactivatedBy: adminUserId,
        deactivationReason: reason,
        updatedAt: new Date().toISOString()
      };

      const result = await userService.updateUser(userId, updatedUser);
      
      if (result.success) {
                return { success: true, data: updatedUser };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Reactivate user
   */
  reactivateUser: async (userId, adminUserId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const updatedUser = {
        ...userResult.data,
        isActive: true,
        reactivatedAt: new Date().toISOString(),
        reactivatedBy: adminUserId,
        deactivatedAt: null,
        deactivatedBy: null,
        deactivationReason: null,
        updatedAt: new Date().toISOString()
      };

      const result = await userService.updateUser(userId, updatedUser);
      
      if (result.success) {
                return { success: true, data: updatedUser };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Grant extension to user
   */
  grantExtension: async (userId, extensionData, adminUserId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const updatedUser = {
        ...userResult.data,
        hasExtension: true,
        extensionCutoffDate: extensionData.cutoffDate,
        extensionReason: extensionData.reason,
        extensionGrantedBy: adminUserId,
        extensionGrantedAt: new Date().toISOString(),
        cycleEndDate: extensionData.newCycleEndDate || userResult.data.cycleEndDate,
        updatedAt: new Date().toISOString()
      };

      const result = await userService.updateUser(userId, updatedUser);
      
      if (result.success) {
                return { success: true, data: updatedUser };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get user's CEU summary (admin view) - uses direct DynamoDB access
   */
  getUserCEUSummary: async (userId) => {
    try {
      // For admin functions, use direct DynamoDB access to avoid API limitations
      const dynamoCEUService = await import('./dynamoCEUService');
      const ceuResult = await dynamoCEUService.default.getUserCEUs(userId);
      
      if (!ceuResult.success) {
        return { success: false, error: 'Could not load CEUs' };
      }

      const ceus = ceuResult.data;
      const currentCycleCEUs = ceus.filter(ceu => ceu.isCurrentCycle === true);
      const archivedCEUs = ceus.filter(ceu => ceu.isCurrentCycle === false);
      
      const totalHours = ceus.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
      const currentCycleHours = currentCycleCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);

      return {
        success: true,
        data: {
          totalCEUs: ceus.length,
          totalHours: totalHours.toFixed(1),
          currentCycleCEUs: currentCycleCEUs.length,
          currentCycleHours: currentCycleHours.toFixed(1),
          archivedCEUs: archivedCEUs.length,
          lastCEUDate: ceus.length > 0 ? ceus.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date : null,
          ceus: ceus // Return the actual CEU data for admin use
        }
      };
    } catch (error) {
      console.error('Admin getUserCEUSummary error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get CEUs for a specific user (admin only) - uses direct DynamoDB access
   */
  getUserCEUs: async (userId) => {
    try {
      // For admin functions, use direct DynamoDB access to avoid API limitations
      const dynamoCEUService = await import('./dynamoCEUService');
      const result = await dynamoCEUService.default.getUserCEUs(userId);
      
      if (result.success) {
        return {
          success: true,
          data: result.data || []
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to load CEUs',
          data: []
        };
      }
    } catch (error) {
      console.error('Admin getUserCEUs error:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  /**
   * Update user role (promote/demote admin)
   */
  updateUserRole: async (userId, newRole, adminUserId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const updatedUser = {
        ...userResult.data,
        role: newRole,
        isAdmin: newRole === 'admin',
        adminPermissions: newRole === 'admin' ? [
          'manage_users',
          'grant_extensions', 
          'view_all_ceus',
          'export_data',
          'system_settings'
        ] : [],
        roleUpdatedBy: adminUserId,
        roleUpdatedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const result = await userService.updateUser(userId, updatedUser);
      
      if (result.success) {
                return { success: true, data: updatedUser };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Reset user extension status (allow them to use extension again)
   */
  resetUserExtension: async (userId, adminUserId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const updatedUser = {
        ...userResult.data,
        extensionUsed: false,
        extensionGrantedDate: null,
        extensionGrantedBy: null,
        extensionReason: null,
        updatedAt: new Date().toISOString(),
        extensionResetAt: new Date().toISOString(),
        extensionResetBy: adminUserId
      };

      const result = await userService.updateUser(userId, updatedUser);
      
      if (result.success) {
        return { 
          success: true, 
          message: `Extension status reset for ${userResult.data.email || userId}`,
          data: updatedUser 
        };
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get user extension status
   */
  getUserExtensionStatus: async (userId) => {
    try {
      const userResult = await userService.getUser(userId);
      if (!userResult.success) {
        return { success: false, error: 'User not found' };
      }

      const user = userResult.data;
      return {
        success: true,
        data: {
          userId: user.userId,
          email: user.email,
          name: user.name,
          extensionUsed: user.extensionUsed || false,
          extensionGrantedDate: user.extensionGrantedDate || null,
          extensionGrantedBy: user.extensionGrantedBy || null,
          extensionReason: user.extensionReason || null,
          currentCycleEnd: user.currentCycleEnd || null,
          extensionResetAt: user.extensionResetAt || null,
          extensionResetBy: user.extensionResetBy || null
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default adminService;
